import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Portal,
    Tooltip,
    IconButton,
    TextField,
    FormControlLabel,
    Switch,
    CircularProgress,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { toast } from 'react-toastify';
import clientTaxAdvanceYearlyService from '../../../Services/clientTaxAdvanceYearly.service';
import styles from './YearlyTaxAdvanceModal.module.scss';

const YearlyTaxAdvanceModal = ({ open, onClose, caseId, token, t, direction = 'rtl' }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [year, setYear] = useState(new Date().getFullYear());
    const [usePercentage, setUsePercentage] = useState(true);
    const [percentage, setPercentage] = useState('');
    const [amount, setAmount] = useState('');
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        if (!open || !caseId || !token) return;
        setLoading(true);
        try {
            const data = await clientTaxAdvanceYearlyService.getByCaseId(token, caseId);
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
            toast.error(t('FailedToLoadYearlyTaxAdvance'));
        } finally {
            setLoading(false);
        }
    }, [open, caseId, token, t]);

    useEffect(() => {
        load();
    }, [load]);

    const handleSave = async () => {
        if (!caseId || !token) return;
        const y = parseInt(year, 10);
        if (Number.isNaN(y) || y < 1900 || y > 2100) {
            toast.error(t('InvalidYear'));
            return;
        }
        setSaving(true);
        try {
            await clientTaxAdvanceYearlyService.upsert(token, {
                caseId,
                year: y,
                useIncomeTaxAdvancePercentage: usePercentage,
                incomeTaxAdvancePercentage: usePercentage ? parseFloat(percentage || '0') : null,
                incomeTaxAdvanceAmount: !usePercentage ? parseFloat(amount || '0') : null,
            });
            toast.success(t('SavedSuccessfully'));
            await load();
        } catch (e) {
            console.error(e);
            toast.error(t('SaveFailed'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!caseId || !token) return;
        try {
            await clientTaxAdvanceYearlyService.remove(token, id, caseId);
            toast.success(t('DeletedSuccessfully'));
            await load();
        } catch (e) {
            console.error(e);
            toast.error(t('DeleteFailed'));
        }
    };

    const inputPropsStyle = {
        disableUnderline: true,
        className: `custom-input-box-sizing-toggle ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
        sx: {
            height: '42px',
            padding: '0 14px',
            borderRadius: 'var(--xtram-radius-md)',
            background: 'var(--login-input-bg)',
            border: 'none',
            boxSizing: 'border-box',
            '&:hover': {
                background: 'color-mix(in srgb, var(--login-input-bg) 85%, var(--xtram-mirage))',
            },
            '&.Mui-focused, &:focus-within': {
                background: 'var(--xtram-white)',
            },
        },
    };

    const inputStyle = {
        style: {
            textAlign: direction === 'rtl' ? 'right' : 'left',
            direction: direction === 'rtl' ? 'rtl' : 'ltr',
        },
    };

    if (!open) return null;

    return (
        <Portal>
            <div
                className={`${styles.overlay} ${direction === 'rtl' ? styles.rtl : styles.ltr}`}
                onClick={onClose}
            >
                <div dir={direction} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modal}>
                        <div className={styles.scroll}>
                            <div className={styles.header}>
                                <Tooltip title={t('Close') || 'Close'}>
                                    <IconButton onClick={onClose} className={styles.closeIcon}>
                                        <CancelIcon className={styles.closeIcon} />
                                    </IconButton>
                                </Tooltip>
                                <div>{t('ManageYearlyTaxAdvance')}</div>
                                <div />
                            </div>

                            <div className={styles.hint}>
                                {t('ManageYearlyTaxAdvanceHelp')}
                            </div>

                            {loading ? (
                                <div className={styles.loading}>
                                    <CircularProgress className={styles.spinner} size={36} />
                                </div>
                            ) : (
                                <div className={styles.tableWrap}>
                                    <table className={styles.table}>
                                        <thead>
                                            <tr>
                                                <th>{t('Year')}</th>
                                                <th>{t('IncomeTaxAdvancePercentage')}</th>
                                                <th>{t('IncomeTaxAdvanceAmount')}</th>
                                                <th>{t('UseIncomeTaxAdvancePercentage')}</th>
                                                <th>{t('Actions')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map((r) => (
                                                <tr key={r.id}>
                                                    <td>{r.year}</td>
                                                    <td>{r.incomeTaxAdvancePercentage ?? '—'}</td>
                                                    <td>{r.incomeTaxAdvanceAmount ?? '—'}</td>
                                                    <td>{r.useIncomeTaxAdvancePercentage ? t('Yes') : t('No')}</td>
                                                    <td>
                                                        <IconButton
                                                            size="small"
                                                            className={styles.deleteBtn}
                                                            onClick={() => handleDelete(r.id)}
                                                            aria-label="delete"
                                                        >
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </td>
                                                </tr>
                                            ))}
                                            {rows.length === 0 && (
                                                <tr>
                                                    <td colSpan={5} className={styles.emptyCell}>
                                                        {t('NoYearlyRowsYet')}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            <div className={styles.sectionTitle}>{t('AddOrUpdateYear')}</div>

                            <div className={styles.formRow}>
                                <div className={styles.field}>
                                    <div className={styles.title}>{t('Year')}</div>
                                    <TextField
                                        variant="standard"
                                        fullWidth
                                        type="number"
                                        className={styles.styleInput}
                                        value={year}
                                        onChange={(e) => setYear(e.target.value)}
                                        InputProps={inputPropsStyle}
                                        inputProps={{ ...inputStyle.style, inputMode: 'numeric' }}
                                        placeholder={t('Year')}
                                    />
                                </div>
                                <div className={`${styles.field} ${styles.checkbox}`}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={usePercentage}
                                                onChange={(e) => setUsePercentage(e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label={t('UseIncomeTaxAdvancePercentage')}
                                    />
                                </div>
                            </div>

                            <div className={styles.formRow}>
                                {usePercentage ? (
                                    <div className={styles.field}>
                                        <div className={styles.title}>{t('IncomeTaxAdvancePercentage')}</div>
                                        <TextField
                                            variant="standard"
                                            fullWidth
                                            type="number"
                                            className={styles.styleInput}
                                            value={percentage}
                                            onChange={(e) => setPercentage(e.target.value)}
                                            InputProps={inputPropsStyle}
                                            inputProps={{ ...inputStyle.style, inputMode: 'decimal' }}
                                            placeholder={t('IncomeTaxAdvancePercentage')}
                                        />
                                    </div>
                                ) : (
                                    <div className={styles.field}>
                                        <div className={styles.title}>{t('IncomeTaxAdvanceAmount')}</div>
                                        <TextField
                                            variant="standard"
                                            fullWidth
                                            type="number"
                                            className={styles.styleInput}
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                            InputProps={inputPropsStyle}
                                            inputProps={{ ...inputStyle.style, inputMode: 'decimal' }}
                                            placeholder={t('IncomeTaxAdvanceAmount')}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className={styles.footer}>
                                <button type="button" className={styles.cancel} onClick={onClose}>
                                    {t('Close')}
                                </button>
                                <button
                                    type="button"
                                    className={styles.save}
                                    onClick={handleSave}
                                    disabled={saving}
                                >
                                    {saving ? (
                                        <CircularProgress size={22} sx={{ color: 'var(--xtram-white)' }} />
                                    ) : (
                                        t('Save')
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

YearlyTaxAdvanceModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    caseId: PropTypes.string,
    token: PropTypes.string,
    t: PropTypes.func.isRequired,
    direction: PropTypes.string,
};

export default YearlyTaxAdvanceModal;
